/**
 * eventPermissions.ts
 *
 * Shared "can edit event" logic used both for gating the Edit Event
 * button/UI and for protecting the edit route itself, so the two never
 * drift out of sync.
 */

import type { EventItem } from '../api/events';

/** Collaborator roles that grant edit access to the event */
const EDITABLE_COLLABORATOR_ROLES = ['manager', 'editor'];

type EventForPermissionCheck = Pick<
  EventItem,
  'event_creator' | 'event_creator_id' | 'project_collaborators' | 'collaborators'
>;

/**
 * Returns true when the given user is allowed to edit the event — because
 * they created it, because they're a `manager`/`editor` in the event's
 * linked project collaborators, or because they're listed among the
 * event's own collaborators (that list carries no per-collaborator role,
 * so membership alone grants edit access — same as the "canManage" check
 * elsewhere). IDs are compared as strings since the logged-in user id and
 * collaborator id may arrive as either a number or a string depending on
 * the source.
 */
export function canEditEvent(
  event: EventForPermissionCheck | undefined | null,
  loggedInUserId: string | number | undefined | null,
): boolean {
  if (!event || loggedInUserId === undefined || loggedInUserId === null || loggedInUserId === '') {
    return false;
  }

  const userIdStr = String(loggedInUserId);

  const creatorId = event.event_creator_id ?? event.event_creator;
  const isEventCreator = !!creatorId && String(creatorId) === userIdStr;

  const isEditableProjectCollaborator = (event.project_collaborators ?? []).some(
    (collaborator) =>
      String(collaborator.user_id) === userIdStr &&
      EDITABLE_COLLABORATOR_ROLES.includes(collaborator.role),
  );

  const isEventCollaborator = (event.collaborators ?? []).some(
    (collaborator) => String(collaborator.id) === userIdStr,
  );

  return isEventCreator || isEditableProjectCollaborator || isEventCollaborator;
}
